/**
 * The price list: what Naurist sells and what it costs.
 *
 * One endpoint — `GET /api/v1/billing/plans/` — and it is the only place a
 * figure in this feature comes from. The cards, the checkout's subtotal and the
 * total it charges all descend from what this returns, so the price somebody is
 * quoted is the price the backend would raise an invoice for.
 *
 * Money crosses the wire as a decimal string — `"6480.00"` — the way it does on
 * every other response here. It is a number from this file on.
 *
 * A row can carry two prices: what the plan costs, and a `launch` offer inside
 * it. Where there is a launch it is the one on sale — its figures *and* its
 * code — so a card shows what it will charge and buys what it showed. See
 * `offerOn`.
 *
 * Unlike `subscriptionsService` beside it, nothing here is about a workspace:
 * this is the same answer for everybody, which is why it is its own service and
 * why it is not behind `SUBSCRIPTIONS_LIVE`. The endpoint exists.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { CYCLE_LABELS, PRO_FEATURES } from "../data/subscriptionPlans";
import type {
  BillingCycleId,
  PlanOfferResponse,
  PlanResponse,
  SubscriptionPlan,
} from "../types/billing.types";

const MONTHS_IN_YEAR = 12;

/**
 * The intervals this app has a card for, and how long one of them runs.
 *
 * An interval that is not in here — a week, a quarter, whatever is sold next —
 * is dropped rather than read as the nearest thing: every screen in the feature
 * is keyed by `BillingCycleId`, from the checkout's URL to the months the tax
 * is worked out over, and a cycle mapped to the wrong one of the two would
 * quote a year at a month's price. A plan nobody can buy yet is better than one
 * bought at the wrong figure.
 */
const CYCLES: Record<string, { id: BillingCycleId; months: number }> = {
  MONTH: { id: "monthly", months: 1 },
  YEAR: { id: "yearly", months: MONTHS_IN_YEAR },
};

/**
 * Which of this app's two cycles an interval is, or `null` for one it has no
 * card for.
 *
 * Exported because the subscription records name their term the same way — see
 * `toPurchasedSubscription` — and there should be one place that knows a
 * `MONTH` is a monthly plan.
 */
export const cycleOfInterval = (
  interval: string | null | undefined
): BillingCycleId | null => CYCLES[(interval ?? "").toUpperCase()]?.id ?? null;

/**
 * The card the eye should land on: the tinted face, the cyan hairline and the
 * filled button, as `PlanCard` draws them.
 *
 * The year, stated rather than worked out — this is a decision about how the
 * page is laid out and not a claim about the figures, which is exactly how the
 * price list has always had it. Deriving it from whichever cycle saves the most
 * would put the design at the mercy of a price change: a year priced level with
 * the month for a week would quietly leave the page with two plain cards and
 * nothing being recommended, which is not a layout anybody drew.
 *
 * The saving badge is the opposite case and is worked out below, because that
 * one *is* a claim about the figures.
 */
const RECOMMENDED_CYCLE: BillingCycleId = "yearly";

/** Money, to the paisa — the same rounding `pricing` does, for the same
 *  reason: a list price divided by twelve is otherwise ₹66.58333333333333. */
const round2 = (amount: number): number => Math.round(amount * 100) / 100;

/** Decimal string to number, defended: an absent or unparseable figure lands on
 *  0 rather than painting "₹ NaN" onto a price card. */
const toAmount = (raw: string | null | undefined): number => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : 0;
};

/**
 * What the badge claims: what a year on this cycle is discounted by.
 *
 * The term's own discount is what the backend works out and sends —
 * `list_amount` less `amount` — and the subtraction is kept behind it for a row
 * that predates the field. For the year that is already a year of it: listed at
 * ₹9,600, sold at ₹6,480 on the launch offer, so the badge reads "Save ₹3,120".
 * The month's term is a month, so its ₹200 is taken over the twelve the launch
 * price holds for and the badge reads "Save ₹2,400".
 *
 * A year on both, because both cards quote a month — ₹800 struck through
 * against ₹600 and against ₹540 — and two badges over different lengths of time
 * would be two figures that cannot be compared sitting on cards whose whole job
 * is to be compared. Over a year they are the same claim the struck-through
 * prices are already making, twelve times, which is why the figures on one card
 * cannot disagree with each other however the backend re-prices them.
 *
 * A cycle sold at its list price has nothing to boast about and gets `null`,
 * which is what `PlanCard` draws no badge for.
 */
const discountOverAYear = (
  offer: PlanOfferResponse,
  months: number
): number | null => {
  const sent = toAmount(offer.discount_amount);
  const onTerm =
    sent > 0 ? sent : toAmount(offer.list_amount) - toAmount(offer.amount);
  const saved = round2((onTerm * MONTHS_IN_YEAR) / months);

  return saved > 0 ? saved : null;
};

/**
 * Which of a row's two prices is the one on sale.
 *
 * The launch offer where one is running, and the plan's own figures otherwise.
 * All of it comes from whichever wins — including the **code**, which is what
 * makes this more than a display decision: the code is what the quote is asked
 * for and what the purchase names, so a card showing the launch price buys the
 * launch price. Showing one and buying the other is the one way this could go
 * wrong, and it is why nothing below reads a figure off `response` directly.
 */
const offerOn = (response: PlanResponse): PlanOfferResponse =>
  response.launch ?? response;

/**
 * One row of the endpoint as a card this app can draw, or `null` for a cycle it
 * has no card for.
 */
export const toPlan = (response: PlanResponse): SubscriptionPlan | null => {
  const offer = offerOn(response);
  // Off the offer, falling back to the row it sits in: the two always agree,
  // and a launch price is for the same cycle as the plan it is a launch of.
  const cycle =
    CYCLES[offer.interval?.toUpperCase()] ??
    CYCLES[response.interval?.toUpperCase()];
  if (!cycle) return null;

  const recommended = cycle.id === RECOMMENDED_CYCLE;

  return {
    id: cycle.id,
    code: offer.code,
    productName: offer.name || response.name,
    cycleLabel: CYCLE_LABELS[cycle.id],
    pricePerMonth: toAmount(offer.monthly_equivalent),
    cyclePrice: toAmount(offer.amount),
    // The list price arrives for the whole term as well — ₹9,600 for a year —
    // and the card quotes a month, struck through beside the month it is
    // paying. Divided rather than sent per month because the two figures beside
    // each other on the card have to be of the same thing.
    listPricePerMonth: round2(toAmount(offer.list_amount) / cycle.months),
    // Both cards wear it, and both quote a year of it so the pair can be read
    // against each other. Which of the two is being recommended is said by how
    // the badge is filled rather than by the other card going without a figure
    // — see `PlanCard`.
    saving: discountOverAYear(offer, cycle.months),
    recommended,
    // How long the price above holds. Nothing draws it yet; see
    // `SubscriptionPlan`, where the reason it is carried anyway is written
    // down.
    totalCycles: offer.total_cycles ?? null,
    // Ours, not the endpoint's. What the product includes is copy this app
    // writes; the backend sells one product and prices two cycles of it.
    features: PRO_FEATURES,
  };
};

/**
 * Every plan on sale, cheapest commitment first.
 *
 * Ordered here rather than trusted from the wire: the cards are read left to
 * right and the one being recommended should be what the eye arrives at, and a
 * page whose order depends on how the backend happened to build its queryset is
 * a page that rearranges itself the day somebody adds an index. The same
 * reasoning as `fetchSubscriptionInvoices`.
 *
 * The token is passed where there is one but never insisted on: this is a price
 * list, not anybody's data, and a screen that could otherwise draw itself should
 * not fail over a session that has not settled yet.
 */
export async function fetchPlans(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<SubscriptionPlan[]> {
  const rows = await api.get<PlanResponse[] | null>(API_ENDPOINTS.billing.plans, {
    token: await getToken(),
    signal,
  });

  return (rows ?? [])
    .map(toPlan)
    .filter((plan): plan is SubscriptionPlan => plan !== null)
    .sort((a, b) => a.cyclePrice - b.cyclePrice);
}
